import { ACTIVITY_COMPLETENESS_MAP, ACTIVITY_TYPES, COMPLETENESS_TYPES } from '@/constants/ActivityTypes.js';
import Model from '@/models/Model.js';
import moment from '@/plugins/vue-moment.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default class Activity extends Model {
    static entity = 'activities';

    static modelName = 'Activity';

    static fields() {
        return {
            id: this.attr(null),
            prerequisite_id: this.attr(null),
            uuid: this.attr(null),
            activity_settings_id: this.attr(null),
            activity_template_id: this.attr(null),
            activity_type_id: this.attr(null),
            activity_type_key: this.attr(null),
            activity_type: this.attr(null),
            attempts_remaining: this.attr(null),
            contentable_id: this.attr(null),
            contentable_type: this.attr(null),
            contentable_type_entity: this.attr(null),
            course_id: this.attr(null),
            due_at: this.attr(null),
            section_id: this.attr(null),
            grading_scheme_id: this.attr(null),
            points_possible: this.attr(null),
            title: this.attr(null),
            description: this.attr(null),
            is_active: this.attr(null),
            order: this.attr(null),
            media: this.attr(null),
            question_count: this.attr(null),
            remediations: this.attr(null),

            completed: this.attr(null),
            user_submission_count: this.attr(null),
            user_submissions: this.attr([]),

            is_generating: this.attr(null),
            generation_status: this.attr(null),
            generation_error: this.attr(null),

            ...super.fields(),

            course: this.belongsTo('courses', 'course_id'),
            section: this.belongsTo('sections', 'section_id'),
            activity_settings: this.belongsTo('activity_settings', 'activity_settings_id'),
            contentable: this.morphTo('contentable_id', 'contentable_type_entity'),
            activity_content: this.morphTo('contentable_id', 'contentable_type_entity'),
            rubric: this.hasOne('rubrics', 'activity_id'),
            submissions: this.hasMany('activity_submissions', 'activity_id'),
            grades: this.hasMany('gradebook_grades', 'activity_id'),
        };
    }

    get modelName() {
        return 'Activity';
    }

    get router_link() {
        return {
            name: 'activity-view',
            params: { courseId: this.course_id, activityId: this.id },
        };
    }

    get is_late() {
        return this.activity_settings.is_late;
    }

    get due_date() {
        if (this.activity_settings.due_at === null) {
            return null;
        }

        const timezone = this.$store().getters['InstitutionSettings/get']('timezone');

        return moment.utc(this.activity_settings.due_at).tz(timezone);
    }

    get completeness_type() {
        return ACTIVITY_COMPLETENESS_MAP[this.activity_type_key];
    }

    get completeness() {
        if (this.completeness_type === COMPLETENESS_TYPES.BOOLEAN) {
            return this.submissions.length > 0;
        }

        if (
            this.completeness_type === COMPLETENESS_TYPES.PROGRESS &&
            this.activity_type_key === ACTIVITY_TYPES.DISCUSSION_THREAD
        ) {
            // Use activity_content with null guard, fallback to contentable for backwards compatibility
            const content = this.activity_content || this.contentable || {};

            const {
                completed = false,
                marked_complete = false,
                threads = 0,
                replies = 0,
                min_threads: minThreads = 0,
                min_replies: minReplies = 0,
                user_submission_count = 0,
                user_submissions = [],
            } = content;

            const totalRequired = minThreads + minReplies;

            return {
                completed,
                marked_complete,
                threads,
                replies,
                min_threads: minThreads,
                min_replies: minReplies,
                user_submission_count,
                user_submissions,
                progress: totalRequired > 0 ? Math.round(((threads + replies) / totalRequired) * 100) : 0,
            };
        }

        if (this.completeness_type === COMPLETENESS_TYPES.IN_PROGRESS) {
            // Quiz activities: check if user has made any attempts or submissions
            const hasSubmissions = this.submissions && this.submissions.length > 0;
            const hasSubmissionCount = this.user_submission_count > 0;

            // Consider complete if there are any submissions or submission count
            return hasSubmissions || hasSubmissionCount;
        }

        return false;
    }

    static reorder({ activities, course_id }) {
        this.api().post('/api/reorder-remediation-activities', {
            activities,
            course_id,
        });
    }

    static searchable() {
        return ['title', 'course'];
    }

    static queryBuilder() {
        return new QueryBuilder(this.entity);
    }
}
